import { atom } from 'jotai';
import { atomFamily, atomWithStorage } from 'jotai/utils';
import { TractNRI, SubtractNRI } from 'components/nri-table/types';
import { verificationStateAtomFamily } from 'lib/atoms/verification';
import {
  calculateNriVerificationCounts,
  calculateSlotVerificationCounts,
} from './util';

// Simplified equality: compare by slotId (backend guarantees uniqueness)
const tractNriEqual = (a: TractNRI[], b: TractNRI[]) => {
  if (a.length !== b.length) return false;
  return a.every((slot, i) => slot.slotId === b[i].slotId);
};

// AtomFamily for NRI-level verification counts
export const nriVerificationAtomFamily = atomFamily(
  (tractNRI: TractNRI[]) =>
    atom((get) => {
      const getStatus = (subtract: SubtractNRI): boolean | null => {
        const atomKey = `${subtract.nriCalculationId}-nri`;
        const persistedState = get(verificationStateAtomFamily(atomKey));
        return persistedState !== null
          ? persistedState.status
          : (subtract.verifications?.nri.status ?? null);
      };

      return {
        horizontal: calculateNriVerificationCounts(
          tractNRI,
          'horizontal',
          getStatus
        ),
        vertical: calculateNriVerificationCounts(
          tractNRI,
          'vertical',
          getStatus
        ),
      };
    }),
  tractNriEqual
);

// AtomFamily for slot-level verification counts (includes both slot and NRI counts)
export const slotVerificationAtomFamily = atomFamily(
  (tractNRI: TractNRI[]) =>
    atom((get) => {
      const getStatus = (subtract: SubtractNRI): boolean | null => {
        const atomKey = `${subtract.nriCalculationId}-nri`;
        const persistedState = get(verificationStateAtomFamily(atomKey));
        return persistedState !== null
          ? persistedState.status
          : (subtract.verifications?.nri.status ?? null);
      };

      return {
        horizontal: {
          slot: calculateSlotVerificationCounts(
            tractNRI,
            'horizontal',
            getStatus
          ),
          nri: calculateNriVerificationCounts(
            tractNRI,
            'horizontal',
            getStatus
          ),
        },
        vertical: {
          slot: calculateSlotVerificationCounts(
            tractNRI,
            'vertical',
            getStatus
          ),
          nri: calculateNriVerificationCounts(tractNRI, 'vertical', getStatus),
        },
      };
    }),
  tractNriEqual
);

// AtomFamily for "Exclude LTDs" toggle state (INS-7119)
// When ON: Subtracts LTD from Total, greys out LTD column, adjusts verification counts
export const excludeLtdsAtomFamily = atomFamily((tableId: string) =>
  atomWithStorage<boolean>(`${tableId}-exclude-ltds`, false)
);

// AtomFamily for "Show Expired Permits" toggle state (INS-7119)
// When ON: Shows Expired Permit column after Total column
export const showExpiredPermitsAtomFamily = atomFamily((tableId: string) =>
  atomWithStorage<boolean>(`${tableId}-show-expired-permits`, false)
);

// Legacy atom kept for backward compatibility - can be removed in future release
// @deprecated Use excludeLtdsAtomFamily and showExpiredPermitsAtomFamily instead
export const excludeExpiredLtdAtomFamily = atomFamily((tableId: string) =>
  atomWithStorage<boolean>(`${tableId}-exclude-expired-ltd`, false)
);

// AtomFamily for "Show Expired Permits" toggle state for Slot By Formation table (INS-7119)
// When ON: Shows Expired Permit column after Total column
export const showExpiredPermitsFormationAtomFamily = atomFamily(
  (tableId: string) =>
    atomWithStorage<boolean>(`${tableId}-formation-show-expired-permits`, false)
);
